import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Coventry\'s GREEN DfT rating mean I cannot claim?',
    answer: 'No. GREEN means Coventry performs above average on network-level condition and spend — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Their own report admits unclassified roads are "showing signs of deterioration", footways require increasing treatment, and they still filled 4,799 potholes in 2024/25 while projecting 4,250–4,750 more in 2025/26.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 667.34km — 75% of Coventry\'s 887.98km carriageway network. These roads are surveyed on a four-year cycle (25% of the network annually) using walked Detailed Visual Inspection, not annual SCANNER surveys. At the council\'s own treatment-required methodology, 13% of U-road area needed resurfacing in 2024/25, up from 9% in 2022/23. If your incident fell between surveys, network-level GREEN scorecards may not reflect your street.',
  },
  {
    question: 'Why is best practice rated AMBER when condition and spend are GREEN?',
    answer: 'The DfT best-practice scorecard is separate from condition and spend. Coventry\'s transparency report notes data gaps in pre-2021 unclassified surveys, two different U-road condition methodologies (national Indicator 224b vs in-house treatment-required rules), and a move to PAS 2161 from 2026/27. The council states its 224b figures differ from its treatment-set methodology partly because isolated red sub-sections are extended into realistic treatment extents — methodology differences the DfT flags as affecting best-practice scoring.',
  },
  {
    question: 'How big is Coventry\'s funding gap?',
    answer: 'Coventry\'s lifecycle planning calculates an annual need of £21.6m to maintain selected highway assets to design life. Planned maintenance for 2025/26 is projected at £13.61m — an £8.0m shortfall. In 2024/25 they delivered 9.85km of carriageway resurfacing against a calculated need of 16.5km (60%) and 11.5km of surface treatment against 51km needed (23%).',
  },
  {
    question: 'Unclassified roads needing resurfacing rose from 9% to 13% — does that matter?',
    answer: 'It documents deterioration on the road class where most pothole damage occurs. Coventry\'s in-house treatment-required methodology shows U-road area needing resurfacing rose from 9% (2022/23) to 13% (2024/25), while area needing no treatment fell from 65% to 61%. The council\'s own performance comment states: "Coventry\'s Unclassified network is showing signs of deterioration/extents requiring treatments increasing." DfT GREEN U-road condition (88% good on Indicator 224b) measures a different methodology.',
  },
  {
    question: 'Coventry counts 4,799 pothole fills but only 611 "true potholes" — which figure applies?',
    answer: 'Both come from the same council report using different counting methods. Methodology 4 (the council\'s preferred figure) counts true pothole fills plus reactive patching jobs under 10m² — totalling 4,799 in 2024/25. Methodology 1 counts only true pothole schedule items — 611 in 2024/25. The council acknowledges "many interpretations" of pothole counts across local authorities. Either way, reactive workload remains substantial on a GREEN-rated network.',
  },
  {
    question: 'How do I report a pothole to Coventry City Council?',
    answer: 'Report non-urgent potholes via the council\'s online form at coventry.gov.uk/pothole_report. Urgent issues should go to Customer Services. The council aims to repair potholes over 40mm deep within five to twenty working days. Prior reports of the same defect create a record the council had notice — Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function CoventryCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Coventry Pothole Claims | 4,799 Repairs on a Green-Rated Network | Fixtyer"
        description="Coventry City Council earns GREEN DfT scorecards but filled 4,799 potholes in 2024/25, surveys 75% of its network on a four-year cycle, and admits unclassified roads are deteriorating. What that means for your claim."
        canonical="/councils/coventry"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Coventry', url: '/councils/coventry' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Coventry City Council"
        councilSlug="coventry"
        rating="green"
      />
      <SpeakableSchema
        url="/councils/coventry"
        headline="Coventry Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Coventry</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="green" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            4,799 Pothole Repairs in 2024/25 on a Green-Rated Network
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Coventry earns <strong className="text-emerald-400">GREEN</strong> DfT scorecards for overall rating,
            condition and spend — yet its own transparency report records{' '}
            <strong className="text-rose-400">4,799 pothole repairs in 2024/25</strong>, an{' '}
            <strong className="text-amber-400">£8.0m annual funding shortfall</strong> against £21.6m calculated
            need, and deteriorating unclassified roads where 13% now need resurfacing (up from 9%). Best practice
            is <strong className="text-amber-400">AMBER</strong>. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">667km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 75% of the network</div>
            <div className="text-brand-300 text-sm mt-1">
              Surveyed on a four-year cycle while the council admits U-road treatment extents are increasing.
              DfT GREEN condition scorecards reflect SCANNER data and Indicator 224b — not every residential street
              every year.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            887.98km of Roads — Mostly Unclassified
          </h2>
          <p className="text-surface-600 mb-8">
            Network size from Coventry's June 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">887.98km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">667.34km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">75% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">130.47km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">90.17km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Coventry highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">1,446.71km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Dedicated cycleways</td>
                  <td className="px-6 py-4 text-center font-medium">24.64km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Public rights of way</td>
                  <td className="px-6 py-4 text-center font-medium">61.9km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges and structures</td>
                  <td className="px-6 py-4 text-center font-medium">315</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Drainage assets</td>
                  <td className="px-6 py-4 text-center font-medium">In excess of 50,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Classified (A, B & C) roads are surveyed every two years (one direction each year) and unclassified roads are surveyed every four years (25% of the network annually)."
            source="Coventry City Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* DfT scorecard */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            DfT Scorecards — GREEN Overall, AMBER Best Practice
          </h2>
          <p className="text-surface-600 mb-8">
            Department for Transport ratings for 2025/26 — three GREEN scorecards and one AMBER
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full">
              <caption className="sr-only">Coventry City Council DfT road maintenance ratings 2025 to 2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">Scorecard</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-surface-700">Rating</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-surface-700">What it reflects</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-200 text-sm">
                <tr className="bg-emerald-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Overall</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-surface-600">Combined performance across all three areas</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Condition</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-surface-600">
                    A-roads 78% good (vs 69% national), B&amp;C 78% (vs 62%), U-roads 88% on Indicator 224b (vs 83%)
                  </td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-medium text-brand-900">Spend</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="green" /></td>
                  <td className="px-6 py-4 text-surface-600">
                    Projected 2025/26 planned maintenance £13.61m with 88.41% planned vs reactive split
                  </td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-medium text-brand-900">Best Practice</td>
                  <td className="px-6 py-4 text-center"><RatingBadge rating="amber" /></td>
                  <td className="px-6 py-4 text-surface-600">
                    Survey methodology differences, U-road data gaps pre-2021, and dual condition reporting systems
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Coventry publishes U-road condition in two formats — national
              Indicator 224b (used for DfT GREEN scorecards) and an in-house treatment-required methodology showing
              deteriorating resurfacing need (9% to 13% between 2022/23 and 2024/25). The council states differences
              arise partly because 224b counts isolated red sub-sections while treatment-set rules extend these into
              realistic treatment extents. Some pre-2021 survey gaps remain in the PMS system; full U-road coverage
              arrived in 2024/25.
            </p>
          </div>

          <p className="text-sm text-surface-500">
            Source:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>
          </p>
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What GREEN Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER surveys on classified roads — and a separate picture on unclassified roads and footways
          </p>

          {/* Classified roads summary */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-emerald-900 mb-4">Classified roads (220.64km) — SCANNER, every two years</h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-xl p-4 border border-emerald-200 text-center">
                <div className="text-2xl font-bold text-emerald-600">78%</div>
                <div className="text-xs text-surface-600 mt-1">A-roads green (2024/25)</div>
                <div className="text-xs text-surface-500">National avg: 69%</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200 text-center">
                <div className="text-2xl font-bold text-emerald-600">78%</div>
                <div className="text-xs text-surface-600 mt-1">B&amp;C roads green (2024/25)</div>
                <div className="text-xs text-surface-500">National avg: 62%</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200 text-center">
                <div className="text-2xl font-bold text-emerald-600">88%</div>
                <div className="text-xs text-surface-600 mt-1">U-roads green — Indicator 224b</div>
                <div className="text-xs text-surface-500">National avg: 83%</div>
              </div>
            </div>
            <p className="text-emerald-800 text-sm">
              Classified A and B&amp;C roads are stable and above national averages. Indicator 224b shows U-roads
              above average too — but this methodology has no amber band and differs from Coventry's treatment-required
              figures below.
            </p>
          </div>

          {/* U-road treatment required */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads — treatment required (council methodology 2)</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-900">Needs resurfacing</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-900">Surface treatment</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-900">No treatment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">26%</td>
                    <td className="px-4 py-2 text-center">65%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">9%</td>
                    <td className="px-4 py-2 text-center">29%</td>
                    <td className="px-4 py-2 text-center">62%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">13%</td>
                    <td className="px-4 py-2 text-center font-bold">26%</td>
                    <td className="px-4 py-2 text-center font-bold">61%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Resurfacing need rose from 9% to 13% of U-road area in two years. Percentages are by network area, not
              length. National average unavailable for this methodology.
            </p>
          </div>

          {/* Footways */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">Footways (1,446.71km) — treatment required</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-900">Needs reconstruction</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-900">Slurry seal</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-900">No treatment</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">20%</td>
                    <td className="px-4 py-2 text-center">5%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">10%</td>
                    <td className="px-4 py-2 text-center">66%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">25%</td>
                    <td className="px-4 py-2 text-center font-bold">9%</td>
                    <td className="px-4 py-2 text-center font-bold">66%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 text-sm">
              One in four footway areas now need reconstruction. The council notes data post-2024 should be more
              accurate due to full network survey coverage.
            </p>
          </div>

          <QuoteCard
            quote="Coventry's Unclassified network is showing signs of deterioration/extents requiring treatments increasing."
            source="Coventry City Council Local Highways Maintenance Transparency Report (June 2025)"
          />
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend scorecard — but calculated annual need exceeds planned budgets by £8.0m
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800 text-center">
              <div className="text-3xl font-bold text-emerald-400">£21.6m</div>
              <div className="text-brand-300 text-sm mt-1">Calculated annual need</div>
            </div>
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800 text-center">
              <div className="text-3xl font-bold text-white">£13.61m</div>
              <div className="text-brand-300 text-sm mt-1">Projected planned spend 2025/26</div>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30 text-center">
              <div className="text-3xl font-bold text-amber-400">£8.0m</div>
              <div className="text-brand-300 text-sm mt-1">Annual shortfall</div>
            </div>
          </div>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Coventry delivery vs calculated need 2024/25</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Treatment</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Needed</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Delivered</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Coverage</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">Carriageway resurfacing</td>
                  <td className="px-4 py-3 text-center">16.5km</td>
                  <td className="px-4 py-3 text-center">9.85km</td>
                  <td className="px-4 py-3 text-center text-amber-400">60%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Carriageway surface treatment</td>
                  <td className="px-4 py-3 text-center">51km</td>
                  <td className="px-4 py-3 text-center">11.5km</td>
                  <td className="px-4 py-3 text-center text-rose-400">23%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Footway reconstruction</td>
                  <td className="px-4 py-3 text-center">17.55km</td>
                  <td className="px-4 py-3 text-center">10.17km</td>
                  <td className="px-4 py-3 text-center text-amber-400">58%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Footway slurry seal</td>
                  <td className="px-4 py-3 text-center">71.63km</td>
                  <td className="px-4 py-3 text-center">5.63km</td>
                  <td className="px-4 py-3 text-center text-rose-400">8%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Coventry maintains 84–92% planned maintenance spend (vs reactive) across recent years — projected
                88.41% planned in 2025/26. DfT/TfWM standard grant was £4.28m in 2024/25 against total maintenance
                spend of £12.36m, supplemented by internal contributions and bid funding.
              </p>
            </div>
            <div className="bg-amber-500/20 rounded-xl p-6 border border-amber-500/30">
              <h3 className="font-semibold text-white mb-3">Why potholes still form</h3>
              <p className="text-brand-200 text-sm">
                Even at 88% planned spend, the council delivers only 23% of required carriageway surface treatments
                and admits reactive maintenance alone cannot close the funding gap. Their appendix states that even
                combining all reactive and planned funding "there is still not enough to treat all assets that are
                deteriorating."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Reactive Pothole Work
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — methodology 4 (preferred): true potholes plus reactive patching under 10m²
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Counting caveat:</strong> Coventry publishes five pothole-counting methodologies in Appendix C.
              Methodology 4 (preferred) produced 4,799 fills in 2024/25; Methodology 1 (true potholes only) produced
              611. The council acknowledges "many interpretations" of pothole counts across local authorities.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Coventry estimated potholes filled 2020-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (method 4)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2020/21</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">4,095</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">5,418</td>
                  <td className="px-6 py-4 text-center text-amber-600">+32.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">4,313</td>
                  <td className="px-6 py-4 text-center text-amber-600">+5.3%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">4,992</td>
                  <td className="px-6 py-4 text-center text-amber-600">+21.9%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,799</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">+17.2%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">4,250–4,750</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="We also acknowledge that there are many interpretations of how to classify 'number of potholes' and there will be a great deal of inconsistencies across different LAs."
              source="Coventry City Council Local Highways Maintenance Transparency Report (June 2025)"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Still above baseline</h3>
              <p className="text-surface-600 text-sm">
                4,799 fills in 2024/25 remains 17.2% above the 2020/21 baseline despite favourable winter conditions
                expected to reduce 2025/26 volumes slightly. A green-rated network still producing this reactive
                volume indicates defects form between planned treatments — especially on U-roads surveyed every four
                years.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Coventry says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A, B and C roads:</strong> SCANNER surveys every two years (one direction per year)</li>
                <li>• <strong>Unclassified roads:</strong> walked DVI every four years (25% of network annually)</li>
                <li>• <strong>Footways:</strong> 100% coverage on a four-year cycle (25% annually)</li>
                <li>• <strong>Structures:</strong> general inspections every two years; principal every six years</li>
                <li>• <strong>PAS 2161:</strong> mandatory from 2026/27 — five-category classification replacing RAG</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Safety inspections</h3>
              <p className="text-surface-600 text-sm mb-3">
                Coventry undertakes routine safety inspections at intervals based on road hierarchy, traffic volumes
                and pedestrian use. Potholes over 40mm deep are targeted for repair within five to twenty working
                days; other necessary repairs within 40 working days.
              </p>
              <p className="text-surface-600 text-sm">
                Forward works programmes prioritise schemes using condition surveys, hierarchy, historical defect
                frequency, accident claims records and resident feedback.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Coventry must
              maintain public highways. To rely on a <strong className="text-white">Section 58</strong> defence,
              the council must show a reasonable system for inspecting and repairing the specific defect — not
              just publish green DfT scorecards.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your U-road on the four-year survey cycle — and had that section been surveyed recently?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, council portal) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="Even combining both reactive maintenance funding and planned maintenance funding there is still not enough to treat all assets that are deteriorating."
            source="Coventry City Council Local Highways Maintenance Transparency Report (June 2025) — Appendix E"
          />
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Coventry Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the June 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On footway condition</h3>
              <QuoteCard
                quote="Coventry's footway network is showing signs of deterioration/extents requiring treatments increasing. Data post 2024 should be significantly more accurate due to full network coverage with surveys."
                source="Coventry City Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On funding vs asset value</h3>
              <QuoteCard
                quote="The value of Coventry's highway assets have increased by over £514m since 2018/19. In that same time, only £23.6m was received via the DfT/WMCA in the form of HMB/CRSTS. This equates to only 4.6% of the increase in the GRC of highway assets."
                source="Coventry City Council Local Highways Maintenance Transparency Report (June 2025)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On reactive vs planned spending</h3>
              <QuoteCard
                quote="We spend much more annually on planned works (resurfacing etc.) than we do on reactive repairs... Performing zero reactive maintenance would result in a scenario where it is extremely likely The Council will lose all claims."
                source="Coventry City Council Local Highways Maintenance Transparency Report (June 2025) — Appendix E"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Green-Rated Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Coventry is competent on paper — here is how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN overall, condition and spend DfT scorecards
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Classified roads above national averages on SCANNER data
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 84–92% planned maintenance spend across recent years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Data-driven forward works programme and JCB PotholePro permanent patching
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Transparent reporting with five pothole-counting methodologies published
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect a well-prepared Section 58 defence. Generic "council neglect" arguments will not land.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best-practice scorecard from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 667km of U-roads — 75% of network — on a four-year survey cycle
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-road resurfacing need rose from 9% to 13% on council's own methodology
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 4,799 pothole repairs in 2024/25 — 17.2% above 2020/21 baseline
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Only 23% of required carriageway surface treatment delivered in 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £8.0m annual funding shortfall against £21.6m calculated need
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN scorecards and high planned-maintenance ratios, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, the four-year survey gap is your strongest structural argument</li>
              <li>• Whether the council can produce inspection records for your street, not just network-wide averages</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Coventry's
              own transparency data where it helps you — without pretending the council is failing overall.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Coventry City Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us
            when you reported it so we can reference it in your pack.
          </p>
          <a
            href="https://www.coventry.gov.uk/pothole_report"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a pothole — coventry.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Coventry?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A green-rated council demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No four-year U-road survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No dual-methodology condition caveat</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ U-road deterioration documented (9% to 13%)</li>
                <li>✅ Four-year survey cycle argued</li>
                <li>✅ 4,799 pothole repairs in 2024/25 cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Coventry</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=coventry"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Coventry Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.coventry.gov.uk/downloads/file/46799/dft-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Coventry City Council Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
